/**
 * @file index.tsx
 *
 * @fileoverview Renders a embedded youtube video.
 */
import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { SERVICES } from '~/jobiqo/lib/cmp';
import { checkUserConsentForService } from '~/jobiqo/services/cmp';
import { EmbeddedYoutubeParagraph } from '~/jobiqo/types/types';
import CMPServiceCheckbox from '../../CMPserviceCheckbox';
import EmbeddedVideo from '~/jobiqo/components/Landingpage/EmbeddedYoutube/components/EmbeddedVideo';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { Box } from '~/vcl/atoms/07-box';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { Grid } from '~/vcl/atoms/06-layout/04-grid';
import { Cell } from '~/vcl/atoms/06-layout/04-grid/01-cell';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { AlignedCenter } from '~/vcl/atoms/06-layout/01-alignment/01-align-center';

interface EmbeddedYoutubeProps {
  row: EmbeddedYoutubeParagraph;
  maxContainerSize?: 'small' | 'medium' | 'large' | 'xLarge';
}

export const EmbeddedYoutube = ({
  row,
  maxContainerSize,
}: EmbeddedYoutubeProps) => {
  const userContext: UserContextInterface = useContext(UserContext);

  const userConsentedToYoutube = checkUserConsentForService(
    userContext?.consents,
    SERVICES.Youtube,
  );

  return (
    <Box
      boxShadow="none"
      borderRadius="0px"
      paddingTop={[5, row.spacing ?? 11]}
      paddingBottom={[5, row.spacing ?? 11]}
      marginTop={0}
      backgroundColorOverride={
        row.background ? `var(--color-${row.background})` : 'transparent'
      }
    >
      <Main {...(maxContainerSize ? { size: maxContainerSize } : null)}>
        <Grid gap={[6, 11]} alignItems="center" columns={[1, 5]}>
          <Cell gridColumn={'span 2'}>
            <FlexItems>
              <FlexItem marginRight={1}>
                <Text fontSize={[5, 6]} lineHeight={[5, 6]} fontWeight="bold">
                  {(row as any).embeddedYoutubeTitle ??
                    (row as any).embeddedYoutubeTitle}
                </Text>
                <Spacing size="small" />
                <Text fontSize={[4, 5]} lineHeight={[4, 5]} color="gray1">
                  {row.link?.text}
                </Text>
              </FlexItem>
            </FlexItems>
          </Cell>
          {row.link.url && (
            <Cell gridColumn={'span 3'}>
              <Box>
                <FlexItems justifyContent="center">
                  {userConsentedToYoutube ? (
                    <EmbeddedVideo
                      link={row.link}
                      height={row?.height}
                      width={row?.width}
                    />
                  ) : (
                    <AlignedCenter>
                      <Text marginTop="0">
                        <FormattedMessage
                          id="youtube_landing_page_consent"
                          defaultMessage="Consent to this service"
                        />
                      </Text>
                      <Spacing />
                      <CMPServiceCheckbox serviceId={SERVICES.Youtube} />
                    </AlignedCenter>
                  )}
                </FlexItems>
              </Box>
            </Cell>
          )}
        </Grid>
      </Main>
    </Box>
  );
};
export default EmbeddedYoutube;
